import React, { FC, useEffect, useState } from 'react';
import Alert from '@amzn/meridian/alert/alert';
import { CategoryManagementEvent } from 'src/constants/events';
import { NotificationMessageProps, NotificationToast } from 'src/types/NotificationType';
import Toaster from '@amzn/meridian/toaster';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_TIME_OUT } from 'src/constants/values';
import { REMEMBER_TO_SAVE_WARN_MSG, SAVE_BTN_CLICKED } from '@constants/strings';

const NotificationBanner: FC = () => {
  const [toasts, setToasts] = useState<NotificationToast[]>([]);
  const onCloseToast = (id: string) => setToasts(toasts.filter((t) => t.id !== id));

  useEffect(() => {
    const onOpenToast = (event: NotificationMessageProps) => {
      const timeout = event.autoHideDuration ?? DEFAULT_TIME_OUT;
      const newToast: NotificationToast = {
        id: uuidv4(),
        message: event.message,
        severity: event.severity,
        timeout: event.persist ? 0 : timeout,
      };
      setToasts((prev) => [...prev, newToast]);
    };
    const eventHandler = ((event: CustomEvent<NotificationMessageProps>) => {
      if (event.detail.message === SAVE_BTN_CLICKED) {
        // Close SaveWarningBanner and not show banner for SaveBtnClick Event
        setToasts(toasts.filter((t) => REMEMBER_TO_SAVE_WARN_MSG !== t.message));
      } else if (toasts.length > 0) {
        // dedup any same messages
        const lastNotification = toasts[toasts.length - 1];
        if (lastNotification.message !== event.detail.message) {
          onOpenToast(event.detail);
        }
      } else {
        onOpenToast(event.detail);
      }
    }) as EventListener;
    window.addEventListener(CategoryManagementEvent.Notification, eventHandler);
    return () => {
      window.removeEventListener(CategoryManagementEvent.Notification, eventHandler);
    };
  }, [toasts]);

  return toasts.length > 0 ? (
    // display toasts one at a time
    <Toaster toasts={[toasts[0]]} onCloseToast={onCloseToast} data-cy="notification-banner">
      {(toast: NotificationToast) => (
        <Alert
          data-cy={`notification-alert-${toast.severity}`}
          type={toast.severity}
          toast
          onClose={toast.onClose}
          closeLabel={`dismiss-alert-${toast.severity}`}
          size="large"
        >
          <span
            style={{
              whiteSpace: 'pre-line',
            }}
          >
            {toast.message}
          </span>
        </Alert>
      )}
    </Toaster>
  ) : null;
};

export default NotificationBanner;
